import React, { useEffect, useRef, useState, useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import {
    Button,
    FormControl,
    FormHelperText,
    Paper,
    Typography,
    makeStyles
} from '@ellucian/react-design-system/core';
import {
    spacing60, spacing40, spacing20, spacing30, spacing50, spacing80, boxShadowError,
    borderWidthThick, colorBorderAlertError, borderRadiusXSmall
} from '@ellucian/react-design-system/core/styles/tokens';
import GlobalLocaleContext from '../GlobalLocale/GlobalLocaleContext';
import { CONSTANTS, routes } from '../Helpers/Configuration';
import isEmailValid from '../Helpers/EmailValidation';
import { useApi } from '../ApiContext';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import {
    Form,
    FormTextField,
    clearForm,
    clearForceShowErrors
} from '@nsr/react-form';
import { useSelector, batch, useDispatch } from 'react-redux';
import retrievalReducer from './retrievalSlice';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';
import useRecaptcha from '../Helpers/useRecaptcha';

const useStyles = makeStyles((theme) => {
    return ({
        root: {
            padding: spacing40,
            margin: `${spacing80} auto 0 auto`,
            maxWidth: '800px',
        },
        content: {
            marginTop: spacing40,
            padding: spacing40
        },
        formTitle: {
            marginBottom: spacing20
        },
        detailsText: {
            marginBottom: spacing40
        },
        standardField: {
            marginBottom: spacing40
        },
        captchaContainer: {
            display: 'flex',
            flexDirection: 'row-reverse',
            marginBottom: spacing40,
        },
        notVerified: {
            border: `${borderWidthThick} solid ${colorBorderAlertError}`,
            borderRadius: borderRadiusXSmall,
            boxShadow: boxShadowError,
            padding: spacing20,
            display: 'inline-block'
        },
        [theme.breakpoints.up('md')]: {
            root: {
                padding: spacing50
            },
            content: {
                padding: spacing50
            },
            formTitle: {
                marginBottom: spacing30
            },
            detailsText: {
                marginBottom: spacing50
            },
            standardField: {
                marginBottom: spacing50
            },
            notVerified: {
                padding: spacing40
            }
        },
        [theme.breakpoints.up('lg')]: {
            root: {
                padding: spacing60
            },
            content: {
                padding: spacing60
            },
            formTitle: {
                marginBottom: spacing40
            },
            standardField: {
                marginBottom: spacing60
            }
        },
        continueButton: {
            display: 'flex',
            flexDirection: 'row-reverse'
        },
    });
});

const RetrievalWrapper = props => {
    const additionalSlices = useRef({
        retrieval: retrievalReducer,
    });
    return (
        <Form createNewStore={true} additionalSlices={additionalSlices}>
            <RetrievalForm {...props} />
        </Form>
    );
};
RetrievalWrapper.propTypes = {
    children: PropTypes.node,
};

const RetrievalForm = props => {
    const intl = useIntl();
    const { isTimedout } = props;
    const classes = useStyles();
    const api = useApi();
    const history = useHistory();
    const snackbar = useSnackbar();
    const dispatch = useDispatch();
    const [captchaError, setCaptchaError] = useState(false);
    const { communicationTypes, inquiryTypes, recaptchaActions } = CONSTANTS;
    const { globalLocale } = useContext(GlobalLocaleContext);
    const { globalSettings } = window.PRELOADED_STATE;
    const { recaptcha } = globalSettings;
    const { getRecaptchaToken } = useRecaptcha(!recaptcha?.recaptchaToggle, recaptcha?.recaptchaClientToken);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateEmail = (value) => {
        return isEmailValid(value) ? undefined : intl.formatMessage({ id: 'invalidEmailFormatTextId' });
    };

    useEffect(() => {
        if (isTimedout) {
            batch(() => {
                dispatch(clearForceShowErrors());
                dispatch(clearForm());
            });}
    }, [isTimedout]);

    const onSubmit = async values => {
        setIsSubmitting(true);
        const model = {
            email: currentEmail || '',
            phoneNumber: values[communicationTypes.text] || '',
            status: inquiryTypes.app,
            locale: globalLocale
        };

        const token = await getRecaptchaToken(recaptchaActions.retrieve);
        if (!token && recaptcha?.recaptchaToggle) {
            setIsSubmitting(false);
            setCaptchaError(true);
            return;
        }
        try {
            const params = new URLSearchParams(window.location.search);
            const bypass = params.get('bypass') === 'true';
            await api.inquiries.retrieve(model, token, recaptchaActions.retrieve, bypass);
            const state = {
                inquiry: {
                    status: inquiryTypes.app,
                },
                prospect: model,
            };
            const location = {
                pathname: routes.retrievalSubmitted,
                state:  state
            };
            history.replace(location);
        } catch (e) {
            let msg = '';
            if (e.response) {
                const errorData = e.response.data;
                if (errorData.msg) {
                    msg = e.response.data.msg;
                } else {
                    msg = <FormattedMessage id="retrievalRecaptchaThresholdError" values={{ score: errorData.score, threshold: errorData.threshold }} />;
                }
                snackbar.error(msg);
            } else {
                msg = e.errorText;
                if (!msg && e.formatMessageId) {
                    msg = <FormattedMessage id={e.formatMessageId} />;
                }
            }
            snackbar.error(msg || <FormattedMessage id={'adminGeneralErrorMessage'} />);
        } finally {            
            setIsSubmitting(false);
        }
    };

    const selectErrors = state => state.form.errors;
    const errors = useSelector(selectErrors);

    const selectValue = state => state.form.values[communicationTypes.email];
    const currentEmail = useSelector(selectValue);

    return (
        <div className={classes.root}>
            <Paper className={classes.content}>
                <main role='main'>
                    <div>
                        <Typography variant="h1" className={classes.formTitle}>
                            <strong><FormattedMessage id={'applicationRetrievalName'} /></strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary" className={classes.detailsText}>
                            <span><FormattedMessage id={'applicationRetrievalInstructions'} /></span>
                        </Typography>
                        <Typography variant="body1" className={classes.standardField}>
                            <HtmlFormattedMessage descriptor={{ id: 'applicationRetrievalDescription' }} />

                        </Typography>
                        <div className={classes.standardField}>
                            <FormControl component="fieldset">
                                <div className={classes.standardField}>
                                    <FormTextField
                                        name={communicationTypes.email}
                                        fullWidth={true}
                                        label={intl.formatMessage({ id: 'applicationRetrievalEmailLabel' })}
                                        validate={validateEmail}
                                        required
                                        requiredErrorTextId={intl.formatMessage({ id: 'requiredFieldErrorTextId' })}
                                    />
                                </div>
                                <div className={classes.captchaContainer}>
                                    {captchaError ? <FormHelperText error={true}><FormattedMessage id="captchaValidationError" /></FormHelperText> : null}
                                </div>
                            </FormControl>
                            <div className={classes.continueButton}>
                                <Button
                                    onClick={onSubmit}
                                    type='submit'
                                    disabled={isSubmitting || (errors?.email ? true : errors?.email ? true : false)}
                                >
                                    <FormattedMessage id="continue" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </main>
            </Paper>
        </div>
    );
};

RetrievalForm.propTypes = {
    isTimedout: PropTypes.bool.isRequired,
};

export default RetrievalWrapper;
